import axios from 'axios';
import store from '../store';
import { logoutRequest } from '../modules/login/services/LoginActions';
import { getCookie, setCookie } from '../store/localStorage';

// Configure axios defaults globally
axios.defaults.withCredentials = true;

const baseURL = window.apiBaseUrl;

// Requests made before the user is signed in. Their 401s mean "login failed", not "session expired".
const SIGN_IN_ENDPOINTS = [
    'Login/UserLogin',
    'Login/VerifyMfa',
    'Login/ForgetPassword',
    'Login/ForgetPasswordReset'
];

const apiClient = axios.create({
    baseURL,
    withCredentials: true, // Enable cookies/session handling for backend
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    }
});

// Request interceptor to add auth token and custom headers
apiClient.interceptors.request.use(
    (config) => {
        const authToken = getCookie('authToken');
        const state = store.getState();
        const selectedRole = state?.common?.selectedRole;
        const userData = state?.auth?.userData;
        const authorizedUser = state?.common?.selectedAuthorizedUser;

        if (authToken) {
            config.headers.Authorization = `Bearer ${authToken}`;
        }

        // Ensure Content-Type is set for POST requests
        if (config.method === 'POST' && !config.headers['Content-Type']) {
            config.headers['Content-Type'] = 'application/json';
        }

        if (selectedRole?.code) {
            config.headers.CurrentRoleCode = selectedRole.code;
        }

        if (authorizedUser?.userId) {
            config.headers.LoggedInUserID = authorizedUser.userId;
        } else if (userData?.userId) {
            config.headers.LoggedInUserID = userData.userId;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle common errors
// The API refreshes an expired access token on any authorized request and returns the new one
// in these headers — including when that request itself then fails (400/403/404...). Saving it
// only on success would keep sending the expired token and burn through refresh tokens.
const saveRefreshedToken = (headers) => {
    const newToken = headers?.['x-refreshed-token'];
    const newExpiry = headers?.['x-token-expiry'];

    if (newToken) {
        setCookie('authToken', newToken);
        setCookie('tokenExpiry', newExpiry);
    }
};

apiClient.interceptors.response.use(
    (response) => {
        saveRefreshedToken(response.headers);
        return response;
    },
    (error) => {
        saveRefreshedToken(error.response?.headers);

        // A 401 from a sign-in request is a failed login (wrong password, inactive, SSO account...),
        // not an expired session: let the Login page show the API's message instead of logging out.
        const isSignInRequest = SIGN_IN_ENDPOINTS.some(endpoint =>
            (error.config?.url || '').toLowerCase().endsWith(endpoint.toLowerCase()));

        if (error.response?.status === 401 && !isSignInRequest) {
            // Handle unauthorized access
            const state = store.getState();
            const currentReason = state?.auth?.logoutReason;
            // Only dispatch if not already a more specific reason
            if (!currentReason || currentReason === 'unauthorized') {
                store.dispatch(logoutRequest({ reason: 'unauthorized' }));
            }
        }
        return Promise.reject(error);
    }
);

const request = ({ ...options }) => {
    return apiClient(options)
        .then((response) => {
            if (response.status === 200) {
                return response.data;
            }
            throw new Error('Request failed');
        })
        .catch((error) => {
            const errorData = error.response?.data;
            throw error;
        });
};

export const get = (url, params = {}) => request({ url, method: 'GET', params });

export const post = (url, data = {}) => {
    const config = { url, method: 'POST' };

    // Check if data is FormData
    if (data instanceof FormData) {
        config.data = data;
        config.headers = { 'Content-Type': 'multipart/form-data' };
    } else {
        config.data = data;
    }

    return request(config);
};

export const postFileDownload = (url, data = {}) => {
    const config = { url, method: 'POST', data, responseType: 'blob' };
    return request(config);
};

export const put = (url, data = {}) => request({ url, method: 'PUT', data });

export const del = (url, data = {}) => request({ url, method: 'POST', data });